/**
 * Identidad de marca — punto único de verdad.
 * Cambiar el nombre del producto = editar este archivo.
 */

export const BRAND = {
  name: 'Open Dahu',
  shortName: 'Dahu',
  tagline: 'Trivias en vivo, gratis para todos',
  /** "Open" = sin muros de pago. Lo decimos explícitamente para que no se lea como "open source". */
  openClaim: 'Gratis de verdad: sin planes, sin tarjeta, sin límite de partidas.',
  currencyName: 'Dahus',
  currencySymbol: 'ᴅ',
  maxPlayersPerRoom: 50,
} as const;

/**
 * Paleta, derivada del logotipo (íbice blanco sobre degradado azul→naranja).
 *
 * El degradado del logo original interpola en línea recta entre dos colores casi
 * complementarios, y eso produce una banda oliva apagada en el punto medio. Aquí
 * el degradado pasa por un ámbar cálido intermedio: mismos extremos, sin el
 * tramo turbio.
 */
export const GRADIENT = {
  from: '#0EA5E9', // cielo del logo
  via: '#F59E0B', // paso cálido que evita el punto medio oliva
  to: '#F97316', // naranja del logo
  css: 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 22%, #F59E0B 62%, #F97316 100%)',
} as const;

/**
 * Cuarteto de respuesta.
 *
 * Deliberadamente distinto del conjunto triángulo-rojo / rombo-azul /
 * círculo-amarillo / cuadrado-verde, que es imagen comercial registrada de otra
 * plataforma. Aquí cada opción tiene silueta y tono propios: hexágono, estrella,
 * gota y luna sobre cian, violeta, naranja y magenta. Ningún rojo ni verde, y
 * ninguna silueta compartida.
 *
 * El color nunca es el único portador de información: la silueta distingue las
 * opciones para quien no percibe diferencias de tono, y el nombre de la forma se
 * anuncia a los lectores de pantalla.
 */
export type AnswerShapeId = 'hex' | 'star' | 'drop' | 'moon';

export interface AnswerShape {
  id: AnswerShapeId;
  label: string;
  /** Color base en hex, para usos fuera de Tailwind (canvas, gráficos, meta tags). */
  hex: string;
  /** Clases Tailwind del botón en estado interactivo. */
  button: string;
  /** Halo del estado seleccionado. */
  ring: string;
}

export const ANSWER_SHAPES: AnswerShape[] = [
  {
    id: 'hex',
    label: 'Hexágono',
    hex: '#0EA5E9',
    button: 'bg-sky-600 hover:bg-sky-500 border-sky-400',
    ring: 'ring-sky-300',
  },
  {
    id: 'star',
    label: 'Estrella',
    hex: '#8B5CF6',
    button: 'bg-violet-600 hover:bg-violet-500 border-violet-400',
    ring: 'ring-violet-300',
  },
  {
    id: 'drop',
    label: 'Gota',
    hex: '#F97316',
    button: 'bg-ember-600 hover:bg-ember-500 border-ember-400',
    ring: 'ring-ember-300',
  },
  {
    id: 'moon',
    label: 'Luna',
    hex: '#E1467C',
    button: 'bg-magenta-600 hover:bg-magenta-500 border-magenta-400',
    ring: 'ring-magenta-300',
  },
];

/** Colores de equipo — también fuera del cuarteto rojo/azul/amarillo/verde. */
export const TEAM_COLORS = {
  glacier: { label: 'Glaciar', hex: '#0EA5E9', chip: 'bg-sky-600 border-sky-400 text-white' },
  dusk: { label: 'Ocaso', hex: '#8B5CF6', chip: 'bg-violet-600 border-violet-400 text-white' },
  ember: { label: 'Brasa', hex: '#F97316', chip: 'bg-ember-600 border-ember-400 text-white' },
  bloom: { label: 'Flor', hex: '#E1467C', chip: 'bg-magenta-600 border-magenta-400 text-white' },
} as const;

export type TeamId = keyof typeof TEAM_COLORS;
export const TEAM_IDS = Object.keys(TEAM_COLORS) as TeamId[];
